<template>
	<v-card color="rgba(41, 41, 47, 0.8)">
		<v-card-title> {{ $t("EXPORT_AS") }} </v-card-title>
		<v-card-text>
			<v-card-title class="justify-center text-center" style="word-break:break-word;white-space: break-spaces;">
				{{ $t('ExportRoad', { name: name }) }}<br />
				{{ $t('SelectOutputFormat') }}
			</v-card-title>
			<v-radio-group v-model="active">
				<v-radio name="active" :label="$t('LandXML')" :value="0"></v-radio>
				<v-radio name="active" label="IFC" :value="1"></v-radio>
			</v-radio-group>
			<v-row class="ma-0">
				<v-col>
					<v-card-actions class="justify-end">
						<v-btn variant="flat" @click="closeDialog" color="red">
							{{ $t("CANCEL") }}
						</v-btn>
						<v-btn variant="flat" @click="exportData()" color="primary">
							{{ $t("EXPORT_AS") }}
						</v-btn>
					</v-card-actions>
				</v-col>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script lang="ts">
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import { EarthBrainUtils, LandXML } from "earthbrain-viewer";
import { useStore } from "vuex";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../../utils/googleAnalytics.js";
import type { PropType } from "vue";
import type { SolidMeshViewerGeometry } from "./composables/solidMeshUnit.types";
import { useSolidMeshUnitDisplay } from "./composables/useSolidMeshUnitDisplay";

export default {
	name: "ExportSolidMeshDialog",
	props: {
		name: String,
		geometry: {
			type: Object as PropType<SolidMeshViewerGeometry>,
			required: true,
		},
	},
	setup() {
		// Viewer geometry is always metres; converted to the site unit at the export boundary.
		const { toExportGeometry } = useSolidMeshUnitDisplay();
		return { toExportGeometry };
	},
	data: () => ({
		store: useStore(),
		active: 0,
	}),
	methods: {
		resetProperty() {
			this.active = 0;
		},
		closeDialog() {
			this.$emit("close", false);
		},
		// Prevention of zero-vector generation.
		// Although the likelihood of this occurring with circular objects other than spheres is low,
		// it is executed for all types.
		normalizeGeometry() {
			const uniqueGeometry = EarthBrainUtils.removeDuplicateVertices({
				vertices: this.geometry.vertices,
				indices: this.geometry.indices,
			});
			if (uniqueGeometry.vertices.length === 0) {
				return uniqueGeometry;
			}

			EarthBrainUtils.removeInvalidFacesFromGeometry(uniqueGeometry);
			return uniqueGeometry;
		},
		exportData() {
			if (this.active === 0) {
				addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_XML);
				this.exportLandXML();
			} else if (this.active === 1) {
				addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_OBJECT_EXPORT_IFC);
				this.exportIfc();
			}
		},
		async exportLandXML() {
			const siteName = this.getSiteName();
			if (!siteName) {
				notifyError(
					{ $store: this.store },
					`LandXML ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
				);
				this.closeDialog();
				return;
			}

			const exportGeometry = this.toExportGeometry(this.normalizeGeometry());
			// LandXML stores points as (y, x, z)
			const landxmlPointsBuffer = [];
			for (let i = 0; i < exportGeometry.vertices.length; i += 3) {
				landxmlPointsBuffer.push(
					exportGeometry.vertices[i + 1],
					exportGeometry.vertices[i],
					exportGeometry.vertices[i + 2],
				);
			}
			LandXML.exportMesh(
				landxmlPointsBuffer,
				exportGeometry.indices,
				siteName,
				this.createFileName(new Date(), siteName, this.name),
				this.name,
				exportGeometry.distanceUnit,
			);
			this.closeDialog();
		},
		getSiteName() {
			const siteName = this.store.state.site?.site_name;
			return typeof siteName === "string" && siteName.length > 0
				? siteName
				: undefined;
		},
		createFileName(date: Date, siteName: string, objName: string) {
			return `${[
				date.getFullYear(),
				this.padSingleDigit(date.getMonth() + 1),
				this.padSingleDigit(date.getDate()),
			].join("-")} ${[
				this.padSingleDigit(date.getHours()),
				this.padSingleDigit(date.getMinutes()),
				this.padSingleDigit(date.getSeconds()),
			].join("_")}_${siteName}_${objName}`;
		},
		padSingleDigit(num: number) {
			return num.toString().padStart(2, "0");
		},
		async exportIfc() {
			const siteName = this.getSiteName();
			if (!siteName) {
				notifyError(
					{ $store: this.store },
					`IFC ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
				);
				this.closeDialog();
				return;
			}

			const siteId = this.$route.query.siteId;

			this.store.dispatch("updateIsLoading", true);
			try {
				const filename = `${this.createFileName(new Date(), siteName, this.name)}.ifc`;
				const exportGeometry = this.toExportGeometry(this.normalizeGeometry());
				await this.store.dispatch("object/exportIfc", {
					siteId,
					geometry: {
						vertices: exportGeometry.vertices,
						indices: exportGeometry.indices,
					},
					name: this.name,
					filename,
					distanceUnit: exportGeometry.distanceUnit,
					metersPerUnit: exportGeometry.metersPerUnit,
				});
				notifySuccess(
					{ $store: this.store },
					`IFC ${this.$t("EXPORT_AS")} ${this.$t("successful")}`,
				);
			} catch (e: unknown) {
				console.error(e);
				notifyError(
					{ $store: this.store },
					`IFC ${this.$t("EXPORT_AS")} ${this.$t("failed")}`,
				);
			} finally {
				this.store.dispatch("updateIsLoading", false);
				this.closeDialog();
			}
		},
	},
};
</script>
